import axios from 'axios';
import logger from '../utils/logger';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class ProjectService {
  // Basic project operations with intelligent caching
  async getUserProjects(forceRefresh = false) {
    try {
      const url = forceRefresh 
        ? `${API_BASE}/projects?force_refresh=true`
        : `${API_BASE}/projects`;
      
      const response = await axios.get(url);
      const data = response.data;
      
      logger.log(`📦 Projects loaded: ${data.count} projects (cached: ${data.cached})`);
      return data.projects || [];
    } catch (error) {
      logger.error('Error fetching user projects:', error);
      return [];
    }
  }

  async getProjectFiles(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/projects/${projectId}/files`);
      const formattedFiles = this.formatProjectFiles(response.data.files || []);
      return { files: formattedFiles };
    } catch (error) {
      logger.error('Error fetching project files:', error);
      return { files: [] };
    }
  }

  async getSynopsisProjectDetails(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/projects/${projectId}/synopsis-details`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching synopsis project details:', error);
      return {
        metadata: null,
        synopsis: null,
        transcript: null,
        files: []
      };
    }
  }

  async getKeywordProjectDetails(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/projects/${projectId}/keyword-details`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching keyword project details:', error);
      return {
        metadata: null,
        keywords: null,
        transcript: null,
        files: []
      };
    }
  }

  async updateStandaloneTranscript(projectId, source, transcriptText) {
    try {
      const response = await axios.patch(
        `${API_BASE}/projects/${projectId}/standalone-transcript`,
        { source, transcript_text: transcriptText }
      );
      return response.data;
    } catch (error) {
      logger.error('Error updating standalone transcript:', error);
      throw error;
    }
  }

  async setTranscriptionUseForEditing(projectId, source) {
    try {
      console.log('[TranscriptionDetail] setTranscriptionUseForEditing request', { projectId, source });
      const response = await axios.post(
        `${API_BASE}/projects/${projectId}/transcription-use-for-editing`,
        { source }
      );
      console.log('[TranscriptionDetail] setTranscriptionUseForEditing response', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error setting transcription use for editing:', error);
      throw error;
    }
  }

  /**
   * @param {string} projectId
   * @param {{ transcript_text: string, transcript_segments?: Array<{ start: number, end: number, text: string }> | null }} payload
   */
  async updateFinalEditableTranscript(projectId, payload) {
    try {
      const body = {
        transcript_text: payload.transcript_text ?? '',
        ...(payload.transcript_segments != null && { transcript_segments: payload.transcript_segments }),
      };
      const response = await axios.patch(
        `${API_BASE}/projects/${projectId}/final-editable-transcript`,
        body
      );
      return response.data;
    } catch (error) {
      logger.error('Error updating final editable transcript:', error);
      throw error;
    }
  }

  async setKeywordUserShared(projectId, userSharedSource) {
    try {
      const response = await axios.patch(
        `${API_BASE}/projects/${projectId}/keyword-user-shared`,
        { user_shared_source: userSharedSource }
      );
      return response.data;
    } catch (error) {
      logger.error('Error setting keyword user shared source:', error);
      throw error;
    }
  }

  async getSynopsisComparison(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/synopsis/comparison/${projectId}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching synopsis comparison:', error);
      return {
        project: null,
        comparison: null
      };
    }
  }

  // Project management operations
  async createProject(projectData) {
    try {
      const response = await axios.post(`${API_BASE}/projects`, projectData);
      return response.data.project;
    } catch (error) {
      logger.error('Error creating project:', error);
      throw error;
    }
  }

  async searchProjects(query) {
    try {
      const response = await axios.get(`${API_BASE}/projects/search?query=${encodeURIComponent(query)}`);
      return response.data.projects || [];
    } catch (error) {
      logger.error('Error searching projects:', error);
      return [];
    }
  }

  // Project settings operations
  async getProjectSettings(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/projects/${projectId}/settings`);
      return response.data.settings;
    } catch (error) {
      logger.error('Error fetching project settings:', error);
      return null;
    }
  }

  async updateProjectSettings(projectId, settings) {
    try {
      const response = await axios.put(`${API_BASE}/projects/${projectId}/settings`, settings);
      return response.data.settings;
    } catch (error) {
      logger.error('Error updating project settings:', error);
      throw error;
    }
  }

  // Project analytics operations
  async getUserProjectStats() {
    try {
      const response = await axios.get(`${API_BASE}/projects/stats`);
      return response.data.totalProjects || 0;
    } catch (error) {
      logger.error('Error fetching user project stats:', error);
      return 0;
    }
  }

  async updateProject(projectId, projectData) {
    try {
      const response = await axios.put(`${API_BASE}/projects/${projectId}`, projectData);
      return response.data.project;
    } catch (error) {
      throw error;
    }
  }

  async updateTranscript(projectId, transcriptId, transcriptData) {
    try {
      const response = await axios.put(`${API_BASE}/projects/${projectId}/transcripts/${transcriptId}`, transcriptData);
      return response.data.transcript;
    } catch (error) {
      throw error;
    }
  }

  async deleteProject(projectId) {
    try {
      const response = await axios.delete(`${API_BASE}/projects/${projectId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateWorkflowStatus(projectId, workflowStatus) {
    try {
      const response = await axios.patch(`${API_BASE}/projects/${projectId}/workflow-status`, {
        workflow_status: workflowStatus,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getWorkflowSteps(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/projects/${projectId}/workflow-steps`);
      return response.data?.steps ?? [];
    } catch (error) {
      logger.error('Error fetching workflow steps:', error);
      throw error;
    }
  }

  async setWorkflowSteps(projectId, steps) {
    try {
      const response = await axios.put(`${API_BASE}/projects/${projectId}/workflow-steps`, { steps });
      return response.data;
    } catch (error) {
      logger.error('Error saving workflow steps:', error);
      throw error;
    }
  }

  async updateSynopsisVersion(synopsisId, synopsisText) {
    try {
      const payload = { synopsis_text: synopsisText };
      const response = await axios.patch(`${API_BASE}/synopsis/version/${synopsisId}`, payload);
      return response.data;
    } catch (error) {
      logger.error('Error updating synopsis version:', error);
      throw error;
    }
  }

  async updateHumanVersion(synopsisId, humanVersionText) {
    try {
      const payload = { human_version: humanVersionText };
      const response = await axios.patch(`${API_BASE}/synopsis/version/${synopsisId}/human`, payload);
      return response.data;
    } catch (error) {
      logger.error('Error updating human version:', error);
      throw error;
    }
  }

  async approveHumanVersion(synopsisId) {
    try {
      const response = await axios.post(`${API_BASE}/synopsis/version/${synopsisId}/approve`);
      return response.data;
    } catch (error) {
      logger.error('Error approving human version:', error);
      throw error;
    }
  }

  async getTranscriptionProjectDetails(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/projects/${projectId}/transcription-details`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching transcription project details:', error);
      return {
        metadata: null,
        keywords: null,
        transcript: null,
        files: []
      };
    }
  }

  /** Record transcript download (PM or Creator). Fire-and-forget. */
  async recordTranscriptDownload(projectId, format = 'vtt') {
    try {
      await axios.post(`${API_BASE}/projects/${projectId}/transcript-download`, { format });
    } catch (error) {
      logger.warning('Could not record transcript download:', error);
    }
  }

  // Notifications (admin/PM only – server-side; when client submits keyword/synopsis request)
  async getNotifications(unreadOnly = true, limit = 50) {
    try {
      const response = await axios.get(`${API_BASE}/notifications?unread_only=${unreadOnly}&limit=${limit}`);
      return response.data.notifications || [];
    } catch (error) {
      logger.error('Error fetching notifications:', error);
      return [];
    }
  }

  async getUnreadNotificationsCount() {
    try {
      const response = await axios.get(`${API_BASE}/notifications/unread-count`);
      return response.data.count ?? 0;
    } catch (error) {
      return 0;
    }
  }

  async markNotificationRead(notificationId) {
    try {
      await axios.patch(`${API_BASE}/notifications/${notificationId}/read`);
    } catch (error) {
      logger.error('Error marking notification read:', error);
    }
  }

  async deleteNotification(notificationId) {
    try {
      await axios.delete(`${API_BASE}/notifications/${notificationId}`);
    } catch (error) {
      logger.error('Error deleting notification:', error);
      throw error;
    }
  }

  async updateProjectNote(projectId, note) {
    try {
      const payload = { notes: note };
      const response = await axios.patch(`${API_BASE}/projects/${projectId}/notes`, payload);
      logger.log('✅ Project note updated:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error updating project note:', error);
      throw error;
    }
  }

  // Helper to format project data for frontend display
  formatProjectData(projects) {
    return projects.map((project) => {
      const formatted = {
        id: project.id,
        title: project.name || project.title,
        description: project.description,
        status: project.status,
        projectType: project.project_type || project.projectType || 'transcription', // Include project_type
        createdAt: project.created_at,
        updatedAt: project.updated_at,
        fileCount: project.fileCount || project.file_count || project.raw_file_count || project.total_files || project.project_files?.[0]?.count || 0,
        transcriptCount: project.transcriptCount || project.transcript_count || project.raw_transcript_count || project.completed_files || project.project_transcripts?.[0]?.count || 0,
        synopsisCount: project.synopsisCount || project.synopsis_count || project.raw_synopsis_count || 0,
        totalWords: project.total_words || 0,
        // Latest transcript info (if available)
        confidence: project.latest_transcript_confidence,
        language: project.language || project.latest_transcript_language_code,
        transcript: project.latest_transcript_text,
        // New fields for Synopsis Projects list
        creatorName: project.created_by_name || project.creator_name || project.created_by_email?.split('@')[0] || project.user_email?.split('@')[0] || 'Unknown',
        creator_name: project.created_by_name || project.creator_name || project.created_by_email?.split('@')[0] || project.user_email?.split('@')[0],
        transcriptLanguage: project.source_language || project.transcript_language || project.latest_transcript_language_code || project.language,
        transcript_language: project.source_language || project.transcript_language || project.latest_transcript_language_code,
        processingStatus: project.processing_status || project.file_processing_status || 'pending',
        processing_status: project.processing_status || project.file_processing_status,
        // Approval status (synopsis from generated_synopses; keyword/transcription from keyword_approval_status)
        approvalStatus: project.latest_approval_status || project.approval_status || project.latest_synopsis_approval_status || project.keyword_approval_status || null,
        approval_status: project.latest_approval_status || project.approval_status || project.latest_synopsis_approval_status || project.keyword_approval_status || null,
        notes: project.notes || '',
        // Assignee (keyword, synopsis, or transcription from user_tasks)
        assigneeId: project.keyword_assignee_id ?? project.synopsis_assignee_id ?? project.transcription_assignee_id ?? project.assigneeId ?? null,
        assigneeName: project.keyword_assignee_name ?? project.synopsis_assignee_name ?? project.transcription_assignee_name ?? project.assigneeName ?? null,
        // Keyword-generation specific (for list and detail)
        keyword_approval_status: project.keyword_approval_status ?? null,
        keyword_assignee_id: project.keyword_assignee_id ?? null,
        keyword_assignee_name: project.keyword_assignee_name ?? null,
        // Synopsis/transcription assignee (pass through from API for display and debugging)
        synopsis_assignee_id: project.synopsis_assignee_id ?? null,
        synopsis_assignee_name: project.synopsis_assignee_name ?? null,
        transcription_assignee_id: project.transcription_assignee_id ?? null,
        transcription_assignee_name: project.transcription_assignee_name ?? null,
        workflowStatus: project.workflow_status ?? null,
        workflowStatusDisplayName: project.workflow_status_display_name ?? project.workflowStatusDisplayName ?? null,
        created_via: project.created_via ?? null,
      };
      
      return formatted;
    });
  }

  // Helper to format file data for frontend display
  formatProjectFiles(files) {
    return files.map(file => ({
      id: file.id,
      projectId: file.project_id,
      fileName: file.file_name,
      fileType: file.file_type,
      fileSize: file.file_size,
      fileUrl: file.file_url,
      storageBucket: file.storage_bucket,
      gcsBucket: file.gcs_bucket,
      gcsPath: file.gcs_path,
      fileHash: file.file_hash,
      processingStatus: file.processing_status,
      jobId: file.job_id,
      createdAt: file.created_at,
      updatedAt: file.updated_at,
      transcripts: file.project_transcripts || [],
    }));
  }

  // Helper to format transcript data
  formatTranscriptData(transcripts) {
    return transcripts.map(transcript => ({
      id: transcript.id,
      projectFileId: transcript.project_file_id,
      transcriptText: transcript.transcript_text,
      confidence: transcript.confidence,
      languageCode: transcript.language_code,
      processingTimeSeconds: transcript.processing_time_seconds,
      wordCount: transcript.word_count,
      createdAt: transcript.created_at,
      updatedAt: transcript.updated_at,
    }));
  }

  // Helper to format settings data
  formatSettingsData(settings) {
    if (!settings) return null;
    
    return {
      id: settings.id,
      projectId: settings.project_id,
      languageCode: settings.language_code,
      boostLevel: settings.boost_level,
      customPhrases: settings.custom_phrases || [],
      autoProcess: settings.auto_process,
      notificationEnabled: settings.notification_enabled,
      exportFormat: settings.export_format,
      createdAt: settings.created_at,
      updatedAt: settings.updated_at,
    };
  }

  // Helper to format simple stats data
  formatStatsData(totalProjects) {
    return {
      totalProjects: totalProjects || 0,
    };
  }

  // Cache management methods
  async clearUserCache() {
    try {
      const response = await axios.post(`${API_BASE}/projects/cache/clear`);
      logger.log('🗑️ User cache cleared:', response.data.message);
      return response.data;
    } catch (error) {
      logger.error('Error clearing user cache:', error);
      throw error;
    }
  }

  async getCacheStats() {
    try {
      const response = await axios.get(`${API_BASE}/projects/cache/stats`);
      logger.log('📊 Cache stats:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error getting cache stats:', error);
      return null;
    }
  }

  async getAdminConstant(key) {
    try {
      const response = await axios.get(`${API_BASE}/admin/constants/${key}`);
      return response.data;
    } catch (error) {
      logger.error(`Error fetching admin constant ${key}:`, error);
      return null;
    }
  }

  async getOfficialEvaluation(projectId) {
    try {
      const response = await axios.get(`${API_BASE}/synopsis/official-evaluation/${projectId}`);
      return response.data;
    } catch (error) {
      logger.error('Error fetching official evaluation:', error);
      return { evaluation: null };
    }
  }
}

const projectService = new ProjectService();
export default projectService;
